import React, { useEffect, useState } from "react";
import { getStoredUser } from "../../../../utils/permissions";
import { showToast } from "../../../../utils/showToast";
import { soloDigitosCedula, esCedulaCompleta, mensajeErrorCedula } from "../../../../utils/documentoIdentidad";
import UsuarioPermisosTab from "./UsuarioPermisosTab";

export default function UsuarioFormModal({
  open,
  isEdit,
  usuarioId,
  form,
  setForm,
  roles,
  rolesLoading,
  centros,
  centrosLoading,
  onClose,
  onSubmit,
  saving,
  canSubmit,
  usernamePreview,
}) {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (open) document.body.classList.add("modal-open");
    else document.body.classList.remove("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, [open]);

  const [activeTab, setActiveTab] = useState('datos');
  useEffect(() => { if (open) setActiveTab('datos'); }, [open]);

  const currentUser = getStoredUser();
  const isCurrentSuperadmin = currentUser?.rol?.nombre === "superadmin";
  const selectedRoleName =
    roles.find((rol) => String(rol.id) === String(form.rol_id))?.nombre || "";
  const isTargetSuperadmin = selectedRoleName === "superadmin";
  const blockSuperadminEdits = isEdit && isTargetSuperadmin && !isCurrentSuperadmin;

  useEffect(() => {
    if (!open) return;
    if (!blockSuperadminEdits) return;
    if (!form.password) return;
    setForm((prev) => ({ ...prev, password: "" }));
  }, [open, blockSuperadminEdits, form.password, setForm]);

  if (!open) return null;

  const handleSubmit = (event) => {
    if (blockSuperadminEdits) {
      event?.preventDefault?.();
      return;
    }
    if (!esCedulaCompleta(form.documento_identidad)) {
      event?.preventDefault?.();
      showToast({ type: "warning", message: mensajeErrorCedula(form.documento_identidad) });
      return;
    }
    onSubmit?.(event);
  };

  const handleInputChange = (field) => (event) =>
    setForm((prev) => ({ ...prev, [field]: event.target.value }));

  return (
    <div className="modal fade show d-block management-modal usuarios-modal" tabIndex="-1" role="dialog">
      <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div className="modal-content">
          <div className="modal-header border-0">
            <div>
              {/* <p className="dashboard-eyebrow mb-1 text-uppercase">{isEdit ? "Editar registro" : "Nuevo registro"}</p> */}
              <h5 className="modal-title mb-0">{isEdit ? "Editar usuario" : "Registrar usuario"}</h5>
            </div>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          {isEdit && (
            <div className="modal-header border-0 pt-0 pb-0">
              <ul className="nav nav-tabs w-100">
                <li className="nav-item">
                  <button type="button" className={`nav-link${activeTab === 'datos' ? ' active' : ''}`}
                    onClick={() => setActiveTab('datos')}>
                    <i className="bi bi-person me-1" />Datos
                  </button>
                </li>
                <li className="nav-item">
                  <button type="button" className={`nav-link${activeTab === 'permisos' ? ' active' : ''}`}
                    onClick={() => setActiveTab('permisos')}>
                    <i className="bi bi-shield-lock me-1" />Permisos adicionales
                  </button>
                </li>
              </ul>
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {isEdit && activeTab === 'permisos' ? (
                <UsuarioPermisosTab usuarioId={usuarioId} />
              ) : (<>
              {blockSuperadminEdits && (
                <div className="alert alert-warning mb-3" role="alert">
                  Solo otro superadmin puede editar este usuario.
                </div>
              )}
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label dashboard-label">Nombre completo</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.nombre_completo}
                    onChange={handleInputChange("nombre_completo")}
                    required
                    disabled={blockSuperadminEdits}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label dashboard-label">Cédula</label>
                  <input
                    type="text"
                    className="form-control"
                    inputMode="numeric"
                    value={form.documento_identidad}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, documento_identidad: soloDigitosCedula(e.target.value) }))
                    }
                    maxLength={10}
                    required
                    disabled={blockSuperadminEdits}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label dashboard-label">Telefono</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.telefono}
                    onChange={handleInputChange("telefono")}
                    disabled={blockSuperadminEdits}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label dashboard-label">Correo electronico</label>
                  <input
                    type="email"
                    className="form-control"
                    value={form.email}
                    onChange={handleInputChange("email")}
                    required
                    disabled={blockSuperadminEdits}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label dashboard-label">Rol</label>
                  <select
                    className="form-select"
                    value={form.rol_id}
                    onChange={handleInputChange("rol_id")}
                    required
                    disabled={blockSuperadminEdits || (!roles.length && rolesLoading)}
                  >
                    <option value="">Selecciona un rol</option>
                    {roles.map((rol) => (
                      <option key={rol.id} value={rol.id}>
                        {rol.nombre}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label dashboard-label">Centro (opcional)</label>
                  <select
                    className="form-select"
                    value={form.centro_id}
                    onChange={handleInputChange("centro_id")}
                    disabled={blockSuperadminEdits || centrosLoading}
                  >
                    <option value="">Selecciona un centro</option>
                    {centros.map((centro) => (
                      <option key={centro.id} value={String(centro.id)}>
                        {centro.alias || centro.nombre}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label dashboard-label">
                    contraseña temporal <small className="text-muted">{isEdit ? "(opcional)" : "(requerida)"}</small>
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    value={blockSuperadminEdits ? "" : form.password}
                    onChange={handleInputChange("password")}
                    placeholder={
                      blockSuperadminEdits
                        ? "Solo superadmin puede cambiar esta contraseña"
                        : isEdit
                          ? "Ingresa una nueva contraseña solo si deseas cambiarla"
                          : "Ingresa una contraseña temporal"
                    }
                    required={!isEdit}
                    disabled={blockSuperadminEdits}
                  />
                  {blockSuperadminEdits && (
                    <small className="text-muted d-block mt-1">
                      Solo el superadmin puede cambiar la contraseña de otro superadmin.
                    </small>
                  )}
                </div>
              </div>
              <div className="form-check mt-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="usuarioActivo"
                  checked={form.activo}
                  onChange={(e) => setForm((prev) => ({ ...prev, activo: e.target.checked }))}
                  disabled={blockSuperadminEdits}
                />
                <label className="form-check-label" htmlFor="usuarioActivo">
                  Usuario activo
                </label>
              </div>
              <div className="alert alert-light border mt-3 mb-0 usuarios-username-alert" role="alert">
                <p className="mb-1 fw-semibold">Usuario sugerido</p>
                <p className="mb-0">
                  {usernamePreview ? (
                    <code>{usernamePreview}</code>
                  ) : (
                    <span className="text-muted">Completa nombre y documento para generar uno</span>
                  )}
                </p>
              </div>
              </>)}
            </div>
            <div className={`modal-footer border-0 pt-0${isEdit && activeTab === 'permisos' ? ' d-none' : ''}`}>
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                Cancelar
              </button>
              <button
                type="submit"
                className="btn management-btn-primary"
                disabled={saving || !canSubmit || blockSuperadminEdits}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Guardando...
                  </>
                ) : (
                  "Guardar"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

